import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useUserPermissions } from '../contexts/UserPermissionsContext'
import TopBar from './Sidebar'
import FloatingTeamBar from './FloatingTeamBar'
// import StatusPresenceLine from './StatusPresenceLine'
import { PageTransition } from './PageTransition'
import Snowfall from 'react-snowfall'
import { apiClient } from '../common/apiClient'
import { isHomeWorkspacePath } from '../common/homeWorkspace'
import { PolicyReaderHost } from './policies/PolicyReaderHost'
import { MemoReaderHost } from './memo/MemoReaderHost'

interface LayoutProps {
  // AppHeader feature flags
  enableAdminOverride?: boolean
  enableNotifications?: boolean

  // Layout features
  showTopBar?: boolean

  // Additional props
  children?: React.ReactNode
}

// Normalize pathname so modal sub-routes don't trigger page transitions
const getAnimationKey = (pathname: string) => {
  if (isHomeWorkspacePath(pathname)) return '/home-new'
  // /tasks/123 → /tasks (modal opened on MyTasksPage)
  // /minutes/abc/task/123 → /minutes/abc (modal opened on MeetingDetailPage)
  // /system-development/<section> → /system-development (own sidebar handles in-page nav)
  return pathname
    .replace(/^\/tasks\/\d+$/, '/tasks')
    .replace(/^(\/minutes\/[^/]+)\/task\/\d+$/, '$1')
    .replace(/^\/system-development\/.+$/, '/system-development')
}

const AnimatedOutlet: React.FC<{ onExitComplete?: () => void }> = ({ onExitComplete }) => {
  const location = useLocation()
  const element = useOutlet()

  return <PageTransition routeKey={getAnimationKey(location.pathname)} onExitComplete={onExitComplete}>
    {element}
  </PageTransition>
}

/**
 * Shared Layout Component
 *
 * Provides consistent top navigation across pages.
 *
 * Architecture:
 * - Top bar: Persistent navigation
 * - Main: Page content (from AnimatedOutlet or children with motion wrapper)
 *
 * Usage:
 * ```tsx
 * <Layout showTopBar>
 *   <YourPage />
 * </Layout>
 * ```
 */
const Layout: React.FC<LayoutProps> = ({
  enableAdminOverride = false,
  enableNotifications = false,
  showTopBar = false,
  children,
}) => {
  const { user } = useAuth()
  const location = useLocation()
  const isHomePreview = isHomeWorkspacePath(location.pathname)
  const isModernHome = location.pathname === '/' || isHomePreview
  const { userPermissions, isOverride, effectiveUserEmail, setOverrideUser, clearOverride } = useUserPermissions()
  const mainRef = React.useRef<HTMLElement>(null)
  const [teamBarWidth, setTeamBarWidth] = useState(0)
  const [showChristmasSnow, setShowChristmasSnow] = useState(false)
  const [snowfallGloballyEnabled, setSnowfallGloballyEnabled] = useState(true)
  const [teamBarGloballyEnabled, setTeamBarGloballyEnabled] = useState(true)

  // Fetch Christmas snow preference and global settings
  useEffect(() => {
    const fetchSnowSettings = async () => {
      if (!user?.email) return
      try {
        const [prefs, globalSettings] = await Promise.all([
          apiClient.get<{ show_christmas_snow: boolean }>(
            `/api/user-preferences?email=${encodeURIComponent(user.email)}`
          ),
          apiClient.get<{ snowfall_enabled: boolean; team_bar_enabled: boolean }>('/api/santa-hat/settings'),
        ])
        setShowChristmasSnow(prefs.show_christmas_snow ?? false)
        setSnowfallGloballyEnabled(globalSettings.snowfall_enabled ?? true)
        setTeamBarGloballyEnabled(globalSettings.team_bar_enabled ?? true)
      } catch (error) {
        console.error('Error fetching snow settings:', error)
      }
    }
    fetchSnowSettings()
  }, [user?.email])

  // Listen for preference changes from modal
  useEffect(() => {
    const handlePreferenceChange = (e: CustomEvent<{ show_christmas_snow?: boolean }>) => {
      if (e.detail.show_christmas_snow !== undefined) {
        setShowChristmasSnow(e.detail.show_christmas_snow)
      }
    }
    window.addEventListener('user-preferences-changed', handlePreferenceChange as EventListener)
    return () => window.removeEventListener('user-preferences-changed', handlePreferenceChange as EventListener)
  }, [])

  // Listen for santa hat settings updates (including snowfall_enabled)
  useEffect(() => {
    const handleSantaSettingsUpdate = (e: CustomEvent<{ snowfall_enabled?: boolean; team_bar_enabled?: boolean }>) => {
      if (e.detail.snowfall_enabled !== undefined) {
        setSnowfallGloballyEnabled(e.detail.snowfall_enabled)
      }
      if (e.detail.team_bar_enabled !== undefined) {
        setTeamBarGloballyEnabled(e.detail.team_bar_enabled)
      }
    }
    window.addEventListener('santa-hat-settings-updated', handleSantaSettingsUpdate as EventListener)
    return () => window.removeEventListener('santa-hat-settings-updated', handleSantaSettingsUpdate as EventListener)
  }, [])

  const animationKey = getAnimationKey(location.pathname)
  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0)
    }
  }, [animationKey])

  // Listen for team bar width changes (vertical mode acts as right sidebar)
  useEffect(() => {
    const handleTeamBarWidth = (e: CustomEvent<{ width: number }>) => {
      setTeamBarWidth(e.detail.width)
    }
    window.addEventListener('teambar-width-changed', handleTeamBarWidth as EventListener)
    return () => window.removeEventListener('teambar-width-changed', handleTeamBarWidth as EventListener)
  }, [])

  return (
    <div
      className={`relative flex overflow-hidden ${isModernHome ? 'bg-[#f5f6fc]' : 'bg-transparent'}`}
      style={{
        height: '100vh',
        paddingTop: 'var(--top-bar-height, 0px)',
        boxSizing: 'border-box',
      }}
    >
      {/* Subtle Christmas snowfall effect - behind content */}
      {snowfallGloballyEnabled && showChristmasSnow && (
        <Snowfall
          snowflakeCount={150}
          color="rgba(255, 255, 255, 0.75)"
          style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            zIndex: 0,
            pointerEvents: 'none',
          }}
          speed={[0.5, 2.5]}
          wind={[-0.5, 1]}
          radius={[1.5, 4]}
        />
      )}

      {/* Floating team bar */}
      {teamBarGloballyEnabled && !isHomePreview && <FloatingTeamBar />}

      {/* Site-wide Away/Lunch presence line (allowlist-gated, very top of viewport) */}
      {/* <StatusPresenceLine /> */}

      {/* App navigation and content */}
      <div className={`flex h-full flex-1 flex-col overflow-hidden ${isModernHome ? 'bg-[#f5f6fc]' : 'bg-transparent'}`}>
        {showTopBar && (
          <TopBar
            key={effectiveUserEmail}
            userPermissions={userPermissions}
            onSelectUser={setOverrideUser}
            enableAdminOverride={enableAdminOverride}
            userEmail={effectiveUserEmail}
            onClearOverride={clearOverride}
          />
        )}
        {/* Main content - only this area transitions and scrolls */}
        <main
          ref={mainRef}
          className={`main-scrollbar relative flex-1 overflow-y-auto ${isModernHome ? 'bg-[#f5f6fc]' : 'bg-transparent'}`}
          style={{ paddingRight: !isHomePreview && teamBarWidth > 0 ? `${teamBarWidth}px` : undefined }}
        >
          <Suspense fallback={<div className={`h-full ${isModernHome ? 'bg-[#f5f6fc]' : 'bg-transparent'}`} />}>
            {children ? (
              // When children are provided directly (e.g., MemoPage)
              <PageTransition routeKey={location.pathname}>
                {children}
              </PageTransition>
            ) : (
              // When using nested routes with Outlet
              <AnimatedOutlet />
            )}
          </Suspense>
        </main>
      </div>
      <PolicyReaderHost />
      <MemoReaderHost />
    </div>
  )
}

export default Layout
